/**
 * Route gate and home resolution for the internal-only dashboard views.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";

/**
 * Home for a client-facing user, including Clinical Trials admins.
 */
export const CLIENT_LANDING_PATH = "/eligibility";

/** Home for a system superadmin, who can reach every view. */
export const SUPER_ADMIN_LANDING_PATH = "/eligibility";

/**
 * Render an internal-only view, or send a client-facing user back to their home.
 *
 * The view stays in the bundle and in the router; only its audience narrows. A
 * non-superadmin is redirected rather than shown an error, because these paths
 * are also where stale bookmarks and the catch-all route land.
 *
 * @param children - The gated view
 * @returns The view for a superadmin, otherwise a redirect to Eligibility
 */
export function SuperAdminRoute({ children }: { children: ReactElement }) {
  const { user } = useAuth();

  return user?.isSuperAdmin ? (
    children
  ) : (
    <Navigate to={CLIENT_LANDING_PATH} replace />
  );
}

/**
 * Send the signed-in user to the first view they are allowed to see.
 *
 * @returns A redirect to the caller's home view
 */
export function LandingRedirect() {
  const { user } = useAuth();

  return (
    <Navigate
      to={user?.isSuperAdmin ? SUPER_ADMIN_LANDING_PATH : CLIENT_LANDING_PATH}
      replace
    />
  );
}
